// What Neomaris is: one product in three parts (Core aboard, Cloud, and the app
// with the assistant), what it watches, and how it tells the owner. Every line here names
// something the code does today; the Home page shows it with Gallivant RP's
// recorded readings.
import { Link } from 'react-router-dom';

import { Icon } from '../components/ui/Icon';
import { useDocumentTitle } from '../lib/useDocumentTitle';

const PARTS: readonly { icon: string; title: string; body: string }[] = [
  {
    icon: 'gauge',
    title: 'Core, aboard',
    body: 'Neomaris Core runs on a small Linux computer you keep on the boat, usually a Raspberry Pi. It reads the instruments on the boat’s network, keeps the alarms running when the boat is offline, and sends its readings to Cloud.',
  },
  {
    icon: 'reports',
    title: 'Cloud',
    body: 'Neomaris Cloud keeps the boat’s record. It sends alarms to your phone and writes the morning report.',
  },
  {
    icon: 'chats',
    title: 'The app',
    body: 'The Neomaris app and this website show your boat. Ask Neo, the assistant, a question about it and Neo answers in plain words.',
  },
];

const WATCHES: readonly { icon: string; title: string; detail: string }[] = [
  { icon: 'anchor', title: 'Anchor', detail: 'Distance from the anchor and a swing-circle alarm.' },
  {
    icon: 'tide',
    title: 'Bilge',
    detail: 'Pumps and high-water switches, and how often a pump runs.',
  },
  { icon: 'water', title: 'Water', detail: 'Tank levels and how much you use.' },
  { icon: 'battery', title: 'Batteries', detail: 'State of charge, solar and shore power.' },
  { icon: 'engine', title: 'Engines', detail: 'Each run, its hours, coolant and fuel.' },
  { icon: 'wind', title: 'Weather', detail: 'Wind, pressure and the forecast where the boat is.' },
  {
    icon: 'friend',
    title: 'Friend boats',
    detail: 'Where the boats you follow were last heard on the boat tracker (AIS).',
  },
];

const TELLS: readonly { icon: string; title: string; body: string }[] = [
  {
    icon: 'alarm',
    title: 'Alarms',
    body: 'When a reading crosses an alarm, Neomaris sends it to your phone and says what to check first. You can set an alarm by asking Neo.',
  },
  {
    icon: 'reports',
    title: 'Morning report',
    body: 'A short report each morning on the night: the anchor, batteries, bilge, water and weather.',
  },
  {
    icon: 'boards',
    title: 'Boards',
    body: 'Pin any chart from one of Neo’s answers to the board, a page that keeps those charts together for the boat.',
  },
  {
    icon: 'chats',
    title: 'Questions',
    body: 'Ask Neo about the boat in plain words. It answers from the boat’s readings and record, and shows small instrument cards in the chat.',
  },
];

export function Products() {
  useDocumentTitle('Products - Neomaris');

  return (
    <>
      <section className="page-hero">
        <h1 className="reveal">What Neomaris is</h1>
        <p className="reveal reveal-delay-1">
          Neomaris is one product that watches your boat around the clock. It has three parts: Core
          aboard, Cloud, and the app you ask questions in.
        </p>
      </section>

      <div className="products-wrap">
        <section className="products-block reveal" aria-labelledby="products-parts">
          <h2 id="products-parts">Three parts, one product</h2>
          <p>You install Core on the boat. Cloud and the app come with your account.</p>
          <div className="products-grid">
            {PARTS.map((part) => (
              <div className="tb-card products-card" key={part.title}>
                <div className="tb-card__header">
                  <Icon name={part.icon} size={17} />
                  {part.title}
                </div>
                <p>{part.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="products-block reveal" aria-labelledby="products-watches">
          <h2 id="products-watches">What it watches</h2>
          <p>
            Neomaris reads what your boat already measures, from the instruments on its network.
          </p>
          <ul className="tb-list products-watch">
            {WATCHES.map((row) => (
              <li className="tb-list__row" key={row.title}>
                <span className="tb-list__icon">
                  <Icon name={row.icon} size={18} />
                </span>
                <span className="tb-list__body">
                  <span className="products-watch__title">{row.title}</span>
                  <span className="products-watch__detail">{row.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="products-block reveal" aria-labelledby="products-tells">
          <h2 id="products-tells">How it tells you</h2>
          <p>Neomaris stays quiet until something needs you.</p>
          <div className="products-grid products-grid--two">
            {TELLS.map((item) => (
              <div className="tb-card products-card" key={item.title}>
                <div className="tb-card__header">
                  <Icon name={item.icon} size={17} />
                  {item.title}
                </div>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="tb-card page-cta reveal">
          <div>
            <h2>Interested in Neomaris?</h2>
            <p>Write to us and a person will reply.</p>
          </div>
          <div className="page-cta__actions">
            <Link to="/contact" className="btn btn-primary">
              Get in touch
            </Link>
            <Link to="/downloads" className="btn">
              Download Core
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
